import { supabase } from './supabase';
import {
  DEFAULT_MASS_FAMILY_DATA,
  MASS_BALANCE_TEMPLATE,
  MASS_ECCENTRICITY_KEYS,
  MASS_REPEATABILITY_KEYS,
  buildMassBalanceBlock,
  computeMassBalancePointMetrics,
  finiteOrNull,
  massToKg,
  mergeMassFamilyData,
  normalizeMassUnit,
  resolveMassPointStandards,
  standardWeightValues,
  suggestMassBalanceSetpoints,
  validateMassBalanceRows,
} from './mass-calibration-profile';

export {
  getTodayDailyEnvironmentLog,
  upsertTodayDailyEnvironmentLog,
} from './api-pressure-manometer-pilot';

export const DEFAULT_MASS_VALIDITY_MONTHS = 12;
const VALIDITY_DAYS_BY_MONTHS = { 3: 90, 6: 181, 12: 365, 24: 730 };

function sanitizePatch(patch = {}) {
  return Object.fromEntries(Object.entries(patch).filter(([, value]) => (
    value !== undefined && !(typeof value === 'number' && !Number.isFinite(value))
  )));
}

async function currentUserId() {
  return (await supabase.auth.getUser())?.data?.user?.id ?? null;
}

export function resolveMassValidityDays(months = DEFAULT_MASS_VALIDITY_MONTHS) {
  const value = Number(months);
  if (!Number.isFinite(value) || value <= 0) return VALIDITY_DAYS_BY_MONTHS[DEFAULT_MASS_VALIDITY_MONTHS];
  return VALIDITY_DAYS_BY_MONTHS[value] ?? Math.round((365 / 12) * value);
}

export function addMassValidityDaysToIsoDate(isoDate, months = DEFAULT_MASS_VALIDITY_MONTHS) {
  const base = String(isoDate || '').slice(0, 10);
  if (!base) return null;
  const date = new Date(`${base}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() + resolveMassValidityDays(months));
  return date.toISOString().slice(0, 10);
}

async function getTodayEnvironment() {
  const { getTodayDailyEnvironmentLog } = await import('./api-pressure-manometer-pilot');
  try {
    return await getTodayDailyEnvironmentLog();
  } catch {
    return null;
  }
}

export async function createMassBalanceSession({
  instrumentId,
  serviceOrderId = null,
  serviceOrderCode = null,
  certificateType = 'NO_SAC',
  validityMonths = DEFAULT_MASS_VALIDITY_MONTHS,
}) {
  if (!instrumentId) throw new Error('instrumentId es requerido');

  const { data: instrument, error: instrumentError } = await supabase
    .from('instruments')
    .select(`id, tag, lab_id, brand, model, serial, type, unit, resolution, range_min, range_max, tolerance,
             client:client_id ( id, client_number, name, address, locality, province )`)
    .eq('id', instrumentId)
    .single();
  if (instrumentError) throw instrumentError;

  const sourceUnit = normalizeMassUnit(instrument?.unit);
  if (!['kg', 'g', 'gr', 'mg', 't', 'tn', 'lb', 'lbs'].includes(sourceUnit)) {
    throw new Error(`La unidad ${instrument?.unit || '(sin unidad)'} no es compatible con la plantilla de Masa.`);
  }
  const instrumentType = String(instrument?.type || '').toLowerCase();
  if (['masa patrón', 'masa patron', 'pesa patrón', 'pesa patron', 'peso patrón', 'peso patron', 'test weight', 'standard weight']
    .some((needle) => instrumentType.includes(needle))) {
    throw new Error('Esta plantilla es exclusiva para balanzas. Las pesas de cliente requieren el procedimiento específico de masas patrón.');
  }

  const resolutionKg = massToKg(instrument?.resolution, sourceUnit);
  const rangeMinKg = massToKg(instrument?.range_min, sourceUnit);
  const rangeMaxKg = massToKg(instrument?.range_max, sourceUnit);
  const toleranceKg = massToKg(instrument?.tolerance, sourceUnit);
  const environment = await getTodayEnvironment();
  const familyData = mergeMassFamilyData({
    ...DEFAULT_MASS_FAMILY_DATA,
    original_instrument_unit: instrument?.unit || null,
    values_normalized_to: 'kg',
    environment_readings: DEFAULT_MASS_FAMILY_DATA.environment_readings.map((row) => ({
      ...row,
      temp_c: finiteOrNull(environment?.temp_c),
      pressure_hpa: finiteOrNull(environment?.pressure_hpa),
      humidity_rh: finiteOrNull(environment?.humidity_rh),
    })),
  });

  const payload = {
    family: 'mass',
    instrument_id: instrument.id,
    service_order_id: serviceOrderId,
    service_order_code: serviceOrderCode || null,
    certificate_type: certificateType,
    status: 'draft',
    template_code: MASS_BALANCE_TEMPLATE.code,
    template_revision: MASS_BALANCE_TEMPLATE.revision,
    client_number: instrument?.client?.client_number ? String(instrument.client.client_number) : null,
    client_name: instrument?.client?.name || null,
    client_address: instrument?.client?.address || null,
    client_city: instrument?.client?.locality || null,
    client_province: instrument?.client?.province || null,
    instrument_object: instrument?.type || 'Balanza monoplato',
    instrument_brand: instrument?.brand || null,
    instrument_model: instrument?.model || null,
    instrument_serial: instrument?.serial || null,
    instrument_tag: instrument?.tag || null,
    instrument_unit: 'kg',
    instrument_resolution: resolutionKg,
    instrument_range_min: rangeMinKg,
    instrument_range_max: rangeMaxKg,
    instrument_class: toleranceKg,
    ambient_pressure_avg: finiteOrNull(environment?.pressure_hpa),
    ambient_temp_avg: finiteOrNull(environment?.temp_c),
    ambient_humidity_avg: finiteOrNull(environment?.humidity_rh),
    validity_months: Number(validityMonths) || DEFAULT_MASS_VALIDITY_MONTHS,
    family_data: familyData,
    created_by: await currentUserId(),
  };

  const { data: session, error } = await supabase
    .from('calibration_sessions').insert(payload).select('*').single();
  if (error) throw error;
  const { error: blockError } = await supabase.from('calibration_session_blocks').insert({
    ...buildMassBalanceBlock(),
    resolution: resolutionKg,
    session_id: session.id,
  });
  if (blockError) throw blockError;
  return session;
}

export async function listMassBalanceStandardOptions({ onDate } = {}) {
  const { data, error } = await supabase.rpc('list_mass_balance_standard_options', {
    p_on_date: onDate || new Date().toISOString().slice(0, 10),
  });
  if (error) throw error;
  return data || [];
}

export async function getMassStandardConfiguration(sessionId) {
  const { data, error } = await supabase.rpc('get_mass_session_standard_configuration', { p_session_id: sessionId });
  if (error) throw error;
  return data || {};
}

export async function validateMassStandardConfiguration(sessionId) {
  const { data, error } = await supabase.rpc('validate_mass_session_standards', { p_session_id: sessionId });
  if (error) throw error;
  return data || { valid: false, issues: ['No se pudo validar la trazabilidad.'], configuration: {} };
}

export async function configureMassSessionStandards(sessionId, capabilityIds = []) {
  const { data, error } = await supabase.rpc('configure_mass_session_standards', {
    p_session_id: sessionId,
    p_capability_ids: [...new Set(capabilityIds.filter(Boolean))],
  });
  if (error) throw error;
  return data;
}

export async function getMassSession(sessionId) {
  const { data, error } = await supabase.from('calibration_sessions')
    .select('*').eq('id', sessionId).eq('family', 'mass').single();
  if (error) throw error;
  return data;
}

export async function updateMassSession(sessionId, patch) {
  const normalized = sanitizePatch(patch);
  if (normalized.family_data) normalized.family_data = mergeMassFamilyData(normalized.family_data);
  const { data, error } = await supabase.from('calibration_sessions').update({
    ...normalized,
    updated_at: new Date().toISOString(),
    updated_by: await currentUserId(),
  }).eq('id', sessionId).eq('family', 'mass').select('*').single();
  if (error) throw error;
  return data;
}

export async function listMassSessionsByInstrument(instrumentId) {
  const { data, error } = await supabase.from('calibration_sessions').select('*')
    .eq('instrument_id', instrumentId).eq('family', 'mass').order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function listMassBlocks(sessionId) {
  const { data, error } = await supabase.from('calibration_session_blocks').select('*')
    .eq('session_id', sessionId).order('sort_order');
  if (error) throw error;
  return data || [];
}

export async function listMassPoints(sessionId) {
  const { data, error } = await supabase.from('calibration_session_points').select('*')
    .eq('session_id', sessionId).order('point_no').order('series_no');
  if (error) throw error;
  return data || [];
}

export function massPointsToRows(block, points = [], session = {}) {
  const familyData = mergeMassFamilyData(session.family_data);
  const count = Number(block?.point_count) || MASS_BALANCE_TEMPLATE.pointCount;
  const suggested = suggestMassBalanceSetpoints(session.instrument_range_min, session.instrument_range_max, count);
  const keys = [...MASS_REPEATABILITY_KEYS, ...MASS_ECCENTRICITY_KEYS];
  const rows = Array.from({ length: count }, (_, index) => ({
    point_no: index + 1,
    setpoint: suggested[index] ?? '',
    capability_ids: resolveMassPointStandards(index + 1, familyData),
    ...Object.fromEntries(keys.map((key) => [key, ''])),
  }));
  for (const point of points) {
    const row = rows[Number(point.point_no) - 1];
    if (!row || (block?.id && point.block_id !== block.id)) continue;
    const key = String(point.direction || '').toLowerCase();
    if (!keys.includes(key)) continue;
    row.setpoint = point.setpoint ?? row.setpoint;
    row[key] = point.instrument_reading ?? '';
  }
  return rows;
}

export async function saveMassPoints(sessionId, block, rows = [], snapshots = []) {
  if (!sessionId || !block?.id) throw new Error('Sesión y bloque requeridos');
  const keys = [...MASS_REPEATABILITY_KEYS, ...MASS_ECCENTRICITY_KEYS];
  const payload = [];
  for (const row of rows) {
    const selected = snapshots.filter((snapshot) => (row.capability_ids || []).includes(snapshot?.capability?.id));
    const patternKg = selected.length
      ? selected.reduce((sum, snapshot) => sum + (standardWeightValues(snapshot).actualKg || 0), 0)
      : null;
    keys.forEach((key, index) => {
      const reading = finiteOrNull(row[key]);
      if (reading === null) return;
      payload.push({
        session_id: sessionId,
        block_id: block.id,
        point_no: Number(row.point_no),
        series_no: index + 1,
        direction: key.toUpperCase(),
        setpoint: finiteOrNull(row.setpoint),
        pattern_reading: patternKg,
        instrument_reading: reading,
        unit: 'kg',
      });
    });
  }
  const { error: deleteError } = await supabase.from('calibration_session_points').delete()
    .eq('session_id', sessionId).eq('block_id', block.id);
  if (deleteError) throw deleteError;
  if (!payload.length) return [];
  const { data, error } = await supabase.from('calibration_session_points').insert(payload).select('*');
  if (error) throw error;
  return data || [];
}

export async function calculateMassBalanceResults(sessionId) {
  const validation = await validateMassStandardConfiguration(sessionId);
  if (!validation?.valid) throw new Error((validation?.issues || ['La trazabilidad no es válida.']).join(' '));
  const [session, blocks, points] = await Promise.all([
    getMassSession(sessionId), listMassBlocks(sessionId), listMassPoints(sessionId),
  ]);
  const block = blocks.find((item) => item.block_key === 'mass_balance') || blocks[0];
  if (!block) throw new Error('La sesión no tiene el bloque de Masa.');
  const snapshots = validation?.configuration?.weights || [];
  const effectiveSession = {
    ...session,
    family_data: mergeMassFamilyData({
      ...session.family_data,
      standard_configuration: { weights: snapshots },
    }),
  };
  const rows = massPointsToRows(block, points, effectiveSession);
  const issues = validateMassBalanceRows(rows, effectiveSession, snapshots);
  if (issues.length) throw new Error(issues.join(' '));
  const details = rows.map((row) => computeMassBalancePointMetrics(row, effectiveSession, snapshots)).filter(Boolean);
  const results = details.map((metrics) => ({
    session_id: sessionId,
    block_id: block.id,
    point_no: metrics.point_no,
    pattern_mean: metrics.pattern_mean,
    instrument_mean: metrics.instrument_mean,
    error_abs: metrics.error_abs,
    error_pct: metrics.error_pct,
    hysteresis: metrics.eccentricity_error,
    repeatability: metrics.repeatability,
    u_standard: metrics.u_standard,
    u_drift: metrics.u_drift,
    u_resolution: metrics.u_resolution,
    u_repeatability: metrics.u_repeatability,
    u_temp: metrics.u_convection,
    u_other: Math.hypot(metrics.u_eccentricity, metrics.u_air_buoyancy, metrics.u_other),
    uc: metrics.uc,
    k: metrics.k,
    u_expanded: metrics.u_expanded,
    u_reported: metrics.u_reported,
    in_tolerance: metrics.in_tolerance,
  }));
  const { error: deleteError } = await supabase.from('calibration_session_results').delete().eq('session_id', sessionId);
  if (deleteError) throw deleteError;
  const { error } = await supabase.from('calibration_session_results').insert(results);
  if (error) throw error;
  await updateMassSession(sessionId, {
    status: 'calculated',
    family_data: mergeMassFamilyData({ ...effectiveSession.family_data, result_details: details }),
  });
  return results;
}

export async function listMassResults(sessionId) {
  const { data, error } = await supabase.from('calibration_session_results').select('*')
    .eq('session_id', sessionId).order('point_no');
  if (error) throw error;
  return data || [];
}
